import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { useTier } from "@/context/TierContext";
import { getPlanCta } from "@/lib/planCta";
import { Logo } from "@/components/Logo";
import { PhotoLabelSuggestions } from "@/components/PhotoLabelSuggestions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, MapPin, Loader2, Camera, Check, Lock, Plus,
  BedDouble, Bath, CookingPot, Sofa, LayoutGrid, Images, ShieldCheck, RotateCw, Sparkles,
} from "lucide-react";

const ROOM_CHOICES = [
  { key: "Bedroom", label: "Bedroom", icon: BedDouble },
  { key: "Bathroom", label: "Bathroom", icon: Bath },
  { key: "Kitchen", label: "Kitchen", icon: CookingPot },
  { key: "Living room", label: "Living room", icon: Sofa },
  { key: "Other", label: "Other", icon: LayoutGrid },
];

const PHOTO_PROMPTS = [
  { key: "wide", label: "Wide view", hint: "Stand in the doorway and capture the whole room." },
  { key: "walls", label: "Walls and floor", hint: "Pan across the walls, corners, and flooring." },
  { key: "closeup", label: "Close-up or problem area", hint: "Zoom in on any marks, wear, or damage." },
];

const uid = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random()}`);

export default function QuickStart() {
  const navigate = useNavigate();
  const { trialEligible, trialing, isPremium, openUpgrade } = useTier();

  const [step, setStep] = useState("property"); // property | room | photos | review | success
  const [existing, setExisting] = useState([]);
  const [loadingProps, setLoadingProps] = useState(true);
  const [createMode, setCreateMode] = useState(false);
  const [address, setAddress] = useState("");
  const [propertyId, setPropertyId] = useState(null);
  const [roomName, setRoomName] = useState("");
  const [customRoom, setCustomRoom] = useState("");
  const [roomId, setRoomId] = useState(null);
  const [slots, setSlots] = useState([null, null, null]); // each: {photo_id, storage_path}
  const [uploading, setUploading] = useState({});
  const [busy, setBusy] = useState(false);
  const [reviewPhotos, setReviewPhotos] = useState([]);

  const loadReviewPhotos = useCallback(async () => {
    if (!roomId) return;
    try { const { data } = await api.get(`/rooms/${roomId}/photos`); setReviewPhotos(data || []); }
    catch { /* non-blocking — labeling is optional */ }
  }, [roomId]);

  useEffect(() => { if (step === "review") loadReviewPhotos(); }, [step, loadReviewPhotos]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/properties");
        const active = (data || []).filter((p) => !p.is_demo && !p.is_sample && p.archive_status !== "archived");
        setExisting(active);
        setCreateMode(active.length === 0);
      } catch { setCreateMode(true); }
      finally { setLoadingProps(false); }
    })();
  }, []);

  const doneCount = slots.filter(Boolean).length;

  const chooseProperty = async () => {
    setBusy(true);
    try {
      if (createMode) {
        const { data } = await api.post("/properties", {
          address: address.trim(), doc_type: "move_in", import_mode: "live", documented_stage: "moving_in",
        });
        setPropertyId(data.property_id);
      }
      setStep("room");
    } catch (e) {
      if (e?.response?.status !== 402) toast.error("Couldn't set up that property. Please try again.");
      // 402 is handled by the global interceptor (opens the upgrade modal).
    } finally { setBusy(false); }
  };

  const chooseRoom = async () => {
    const name = (roomName === "Other" ? customRoom.trim() : roomName) || "";
    if (!name) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/properties/${propertyId}/rooms`, { name });
      setRoomId(data.room_id);
      setStep("photos");
    } catch { toast.error("Couldn't create that room. Please try again."); }
    finally { setBusy(false); }
  };

  const uploadSlot = useCallback(async (index, file) => {
    if (!file || !roomId) return;
    setUploading((u) => ({ ...u, [index]: true }));
    const prev = slots[index];
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("client_upload_id", uid());
      fd.append("import_mode", "live");
      const { data } = await api.post(`/rooms/${roomId}/photos`, fd);
      setSlots((s) => { const n = [...s]; n[index] = { photo_id: data.photo_id, storage_path: data.storage_path }; return n; });
      if (prev?.photo_id) { api.delete(`/photos/${prev.photo_id}`).catch(() => {}); }
    } catch {
      toast.error("That photo didn't upload — please try again.");
      // A failed upload never counts toward completion (slot stays as it was).
    } finally {
      setUploading((u) => { const n = { ...u }; delete n[index]; return n; });
    }
  }, [roomId, slots]);

  const seal = async () => {
    setBusy(true);
    try {
      await api.post(`/properties/${propertyId}/lock`);
      setStep("success");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't seal the record. Please try again.");
    } finally { setBusy(false); }
  };

  const addAnotherRoom = () => {
    setRoomId(null); setRoomName(""); setCustomRoom(""); setSlots([null, null, null]);
    setStep("room");
  };

  const cta = getPlanCta({ trialEligible, trialing });
  const showUpsell = !isPremium && !trialing && cta.state !== "trialing";

  return (
    <div className="min-h-screen bg-[#EEF2F6] flex flex-col" data-testid="quick-start-page">
      <header className="border-b border-[#E2E8F0] bg-white">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <button data-testid="qs-back-btn" onClick={() => navigate("/dashboard")}
            className="flex items-center gap-2 text-[#475569] hover:text-navy transition-colors">
            <ArrowLeft size={18} /> Dashboard
          </button>
          <Logo />
          <div className="w-20" />
        </div>
      </header>

      <div className="flex-1 flex items-start justify-center px-4 sm:px-6 py-8 sm:py-12">
        <div className="w-full max-w-lg">
          {step !== "success" && (
            <div className="mb-6">
              <p className="text-xs font-semibold uppercase tracking-[0.1em] text-brand">5-minute quick start</p>
              <h1 className="mt-2 font-heading text-3xl sm:text-4xl font-bold tracking-tight text-navy">
                Seal your first room in 5 minutes
              </h1>
              <p className="mt-2 text-[#475569]">
                Start with one room. Add three photos. Create your first timestamped rental record.
              </p>
            </div>
          )}

          {/* STEP: property */}
          {step === "property" && (
            <div>
              {loadingProps ? (
                <div className="flex items-center gap-2 text-[#64748B] text-sm"><Loader2 size={16} className="animate-spin" /> Loading…</div>
              ) : (
                <>
                  {existing.length > 0 && !createMode && (
                    <div className="space-y-2.5">
                      {existing.map((p, i) => (
                        <button key={p.property_id} data-testid={`qs-property-existing-${i}`}
                          onClick={() => { setPropertyId(p.property_id); setStep("room"); }}
                          className="w-full text-left rounded-2xl border-2 border-[#E2E8F0] bg-white p-4 hover:border-brand transition-colors flex items-center gap-3">
                          <span className="grid place-items-center h-10 w-10 rounded-xl bg-[#F1F5F9] text-navy shrink-0"><MapPin size={18} /></span>
                          <span className="min-w-0 flex-1">
                            <span className="block font-semibold text-navy truncate">{p.address}</span>
                            <span className="block text-xs text-[#94A3B8]">{p.room_count} rooms · {p.photo_count} photos</span>
                          </span>
                          <ArrowRight size={16} className="text-[#CBD5E1] shrink-0" />
                        </button>
                      ))}
                      <button data-testid="qs-create-new-toggle" onClick={() => setCreateMode(true)}
                        className="w-full rounded-2xl border-2 border-dashed border-[#CBD5E1] bg-white p-4 text-brand font-medium hover:border-brand transition-colors flex items-center justify-center gap-2">
                        <Plus size={17} /> Document a new place
                      </button>
                    </div>
                  )}

                  {createMode && (
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wider text-[#94A3B8]">Property address</label>
                      <div className="relative mt-1.5">
                        <MapPin size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
                        <Input data-testid="qs-address-input" autoFocus value={address}
                          onChange={(e) => setAddress(e.target.value)}
                          placeholder="123 Main St, Apt 4, City"
                          className="h-14 pl-12 rounded-2xl text-base bg-white border-[#E2E8F0]" />
                      </div>
                      {existing.length > 0 && (
                        <button onClick={() => setCreateMode(false)} className="mt-3 text-sm text-brand hover:underline">
                          ← Use one of my properties instead
                        </button>
                      )}
                    </div>
                  )}

                  <Button data-testid="qs-property-continue" onClick={chooseProperty}
                    disabled={busy || (createMode && address.trim().length < 4)}
                    className="mt-8 w-full h-14 rounded-2xl bg-brand hover:bg-brand-hover text-white text-base font-medium disabled:opacity-50">
                    {busy ? <Loader2 className="animate-spin" size={20} /> : <>Continue <ArrowRight size={18} className="ml-2" /></>}
                  </Button>
                </>
              )}
            </div>
          )}

          {/* STEP: room */}
          {step === "room" && (
            <div>
              <h2 className="font-heading text-xl font-bold text-navy mb-4">Which room?</h2>
              <div className="grid grid-cols-2 gap-3">
                {ROOM_CHOICES.map((r) => (
                  <button key={r.key} data-testid={`qs-room-${r.key.toLowerCase().replace(/\s/g, "-")}`}
                    onClick={() => setRoomName(r.key)}
                    className={`text-left rounded-2xl border-2 p-4 transition-all ${
                      roomName === r.key ? "border-brand bg-brand/[0.04]" : "border-[#E2E8F0] bg-white hover:border-[#CBD5E1]"
                    }`}>
                    <span className={`grid place-items-center h-10 w-10 rounded-xl ${roomName === r.key ? "bg-brand text-white" : "bg-[#F1F5F9] text-[#475569]"}`}>
                      <r.icon size={20} />
                    </span>
                    <span className="mt-2.5 block font-semibold text-navy">{r.label}</span>
                  </button>
                ))}
              </div>
              {roomName === "Other" && (
                <Input data-testid="qs-room-custom-input" value={customRoom} onChange={(e) => setCustomRoom(e.target.value)}
                  placeholder="Name this room (e.g. Garage)" className="mt-4 h-12 rounded-xl bg-white" autoFocus />
              )}
              <Button data-testid="qs-room-continue" onClick={chooseRoom}
                disabled={busy || !roomName || (roomName === "Other" && !customRoom.trim())}
                className="mt-8 w-full h-14 rounded-2xl bg-brand hover:bg-brand-hover text-white text-base font-medium disabled:opacity-50">
                {busy ? <Loader2 className="animate-spin" size={20} /> : <>Continue <ArrowRight size={18} className="ml-2" /></>}
              </Button>
            </div>
          )}

          {/* STEP: photos */}
          {step === "photos" && (
            <div>
              <h2 className="font-heading text-xl font-bold text-navy mb-1">Add three photos</h2>
              <p className="text-sm text-[#475569] mb-4">{doneCount} of 3 added — at least three are needed to seal.</p>
              <div className="space-y-3">
                {PHOTO_PROMPTS.map((p, i) => {
                  const slot = slots[i];
                  const isUp = uploading[i];
                  return (
                    <div key={p.key} data-testid={`qs-photo-slot-${i}`}
                      className="rounded-2xl border-2 border-[#E2E8F0] bg-white p-4 flex items-center gap-4">
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-navy">{i + 1}. {p.label}</p>
                        <p className="text-xs text-[#94A3B8] leading-snug">{p.hint}</p>
                      </div>
                      {slot ? (
                        <div className="flex items-center gap-2 shrink-0">
                          <img src={fileUrl(slot.storage_path)} alt={p.label}
                            className="h-14 w-14 rounded-lg object-cover border border-[#E2E8F0]" />
                          <label className="cursor-pointer text-xs font-medium text-brand hover:underline" data-testid={`qs-photo-retake-${i}`}>
                            <RotateCw size={13} className="inline mr-0.5" /> Retake
                            <input type="file" accept="image/*" capture="environment" className="hidden"
                              onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; uploadSlot(i, f); }} />
                          </label>
                        </div>
                      ) : (
                        <label data-testid={`qs-photo-input-${i}`}
                          className={`shrink-0 cursor-pointer grid place-items-center h-14 w-14 rounded-xl border-2 border-dashed ${isUp ? "border-brand" : "border-[#CBD5E1] hover:border-brand"} text-brand`}>
                          {isUp ? <Loader2 size={20} className="animate-spin" /> : <Camera size={20} />}
                          <input type="file" accept="image/*" capture="environment" className="hidden" disabled={isUp}
                            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; uploadSlot(i, f); }} />
                        </label>
                      )}
                    </div>
                  );
                })}
              </div>
              <Button data-testid="qs-photos-continue" onClick={() => setStep("review")}
                disabled={doneCount < 3}
                className="mt-8 w-full h-14 rounded-2xl bg-brand hover:bg-brand-hover text-white text-base font-medium disabled:opacity-50">
                Review photos <ArrowRight size={18} className="ml-2" />
              </Button>
            </div>
          )}

          {/* STEP: review */}
          {step === "review" && (
            <div>
              <h2 className="font-heading text-xl font-bold text-navy mb-1">Review, then seal</h2>
              <p className="text-sm text-[#475569] mb-4">Sealing creates a timestamped record for this room. You can add more rooms after.</p>
              <div className="grid grid-cols-3 gap-2.5">
                {slots.filter(Boolean).map((s, i) => (
                  <img key={s.photo_id} data-testid={`qs-review-photo-${i}`} src={fileUrl(s.storage_path)} alt=""
                    className="aspect-square w-full rounded-xl object-cover border border-[#E2E8F0]" />
                ))}
              </div>
              <div className="mt-3 flex items-center justify-between">
                <button onClick={() => setStep("photos")} className="text-sm text-brand hover:underline" data-testid="qs-review-edit">
                  ← Edit photos
                </button>
              </div>
              {reviewPhotos.length > 0 && (
                <div className="mt-4" data-testid="qs-label-suggestions">
                  <PhotoLabelSuggestions propertyId={propertyId} photos={reviewPhotos} onChanged={loadReviewPhotos} />
                </div>
              )}
              <Button data-testid="qs-review-seal-btn" onClick={seal} disabled={busy}
                className="mt-8 w-full h-14 rounded-2xl bg-navy hover:bg-navy-hover text-white text-base font-medium disabled:opacity-50">
                {busy ? <Loader2 className="animate-spin" size={20} /> : <><Lock size={18} className="mr-2" /> Seal this room</>}
              </Button>
              <p className="mt-3 text-center text-xs text-[#94A3B8]">TenantSeal is not a law firm and does not provide legal advice.</p>
            </div>
          )}

          {/* STEP: success */}
          {step === "success" && (
            <div data-testid="qs-success">
              <div className="rounded-2xl border border-[#E2E8F0] bg-white p-6 sm:p-8 text-center">
                <span className="grid place-items-center h-14 w-14 rounded-2xl bg-[#177245]/10 text-[#177245] mx-auto">
                  <ShieldCheck size={28} />
                </span>
                <h1 className="mt-4 font-heading text-2xl sm:text-3xl font-bold tracking-tight text-navy">First room sealed</h1>
                <p className="mt-2 text-[#475569]">You now have a timestamped TenantSeal record for this room.</p>

                <div className="mt-6 space-y-2.5">
                  <Button data-testid="qs-continue-full" onClick={() => navigate(`/property/${propertyId}`)}
                    className="w-full h-12 rounded-xl bg-brand hover:bg-brand-hover text-white">
                    Continue full property documentation <ArrowRight size={16} className="ml-1.5" />
                  </Button>
                  <div className="grid grid-cols-2 gap-2.5">
                    <Button data-testid="qs-add-room" variant="outline" onClick={addAnotherRoom}
                      className="h-12 rounded-xl border-[#E2E8F0] text-navy hover:bg-[#F8FAFC]">
                      <Plus size={16} className="mr-1.5" /> Add another room
                    </Button>
                    <Button data-testid="qs-view-photos" variant="outline" onClick={() => navigate(`/property/${propertyId}`)}
                      className="h-12 rounded-xl border-[#E2E8F0] text-navy hover:bg-[#F8FAFC]">
                      <Images size={16} className="mr-1.5" /> View sealed photos
                    </Button>
                  </div>
                </div>
              </div>

              {showUpsell && (
                <div data-testid="qs-upsell" className="mt-4 rounded-2xl border border-brand/20 bg-brand/[0.04] p-5 sm:p-6">
                  <p className="text-[15px] text-[#334155] leading-relaxed">
                    Unlock evidence bundles, verified sharing, comparisons, move-out tools, and deduction review with TenantSeal Plus.
                  </p>
                  <Button data-testid="qs-upsell-cta" onClick={() => openUpgrade("quick_start")}
                    className="mt-4 h-12 px-6 rounded-xl bg-navy hover:bg-navy-hover text-white w-full sm:w-auto">
                    <Sparkles size={16} className="mr-2" /> {cta.cta}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
